<script lang="ts">
  import { ChevronLeft, XCircle } from 'lucide-svelte'
  import { t } from '$lib/translations'
  import { Facebook, Google } from '$components/CustomIcons/LoginIcons'
  import { showLoading } from '$lib/stores/layout'
  import Skeleton from '$lib/components/ui/skeleton/skeleton.svelte'
  import { providerLogin } from '$lib/pocketbase'
  import Pocketbase, { RecordService } from 'pocketbase'
  import { env } from '$env/dynamic/public'
  import type { TypedPocketBase } from '$lib/pocketbase-types'
  import { onMount } from 'svelte'
  import type { UsersResponse } from '$lib/pocketbase-types.js'
  export let errorMessage: string | undefined

  export let isRecoverPasswordForm = false
  export let recoverPasswordSuccess = false

  type allowedProviders = 'google' | 'facebook'

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const loginIcons: { [key in allowedProviders]: any } = {
    google: Google,
    facebook: Facebook
  }

  function isAllowedProvider(providerName: string): providerName is allowedProviders {
    return Object.keys(loginIcons).includes(providerName)
  }

  let pb: TypedPocketBase
  let usersCollection: RecordService<UsersResponse<unknown>>

  onMount(() => {
    pb = new Pocketbase(env.PUBLIC_PB_URL) as TypedPocketBase
    usersCollection = pb?.collection('users')
  })
</script>

<div class="sm:mx-auto sm:w-full sm:max-w-[480px]">
  <div class="bg-white px-6 py-12 shadow sm:px-12">
    {#if !isRecoverPasswordForm && pb !== undefined}
      <div class="grid grid-cols-1 gap-4">
        {#await usersCollection?.listAuthMethods({ $autoCancel: false })}
          <Skeleton class="h-10" />
        {:then methods}
          {#each methods.authProviders as provider}
            {#if isAllowedProvider(provider.name)}
              <button
                on:click={() => providerLogin(provider, usersCollection, pb)}
                class="outline-cs-orange hover:ring-cs-orange flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-orange-50 focus-visible:ring-transparent"
              >
                <svelte:component this={loginIcons[provider.name]} />
                <span class="text-sm font-semibold leading-6">{$t(`common.${provider.name}_login`)}</span>
              </button>
            {/if}
          {/each}
        {:catch}
          <span></span>
        {/await}
      </div>

      <div>
        <div class="relative mt-10">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="w-full border-t border-gray-200"></div>
          </div>
          <div class="relative flex justify-center text-sm font-medium leading-6">
            <span class="bg-white px-6 text-gray-900">{$t('common.email_login')}</span>
          </div>
        </div>
      </div>
    {:else}
      <button on:click={() => (isRecoverPasswordForm = false)} class="outline-cs-orange hover:ring-cs-orange bg-cs-orange rounded-full p-2">
        <ChevronLeft size={20} color="white" />
      </button>
    {/if}

    {#if isRecoverPasswordForm}
      <button on:click={() => (isRecoverPasswordForm = false)} class="outline-cs-orange hover:ring-cs-orange bg-cs-orange rounded-full p-2">
        <ChevronLeft size={20} color="white" />
      </button>
    {/if}

    <form class="mt-6 space-y-6" method="POST" on:submit={() => ($showLoading = true)}>
      {#if !recoverPasswordSuccess || !isRecoverPasswordForm}
        <div>
          <label for="email" class="block text-sm font-medium leading-6 text-gray-900">{$t('common.email')}</label>
          <div class="mt-2">
            <input
              id="email"
              name="email"
              type="email"
              autocomplete="email"
              required
              class="block w-full outline-cs-orange rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-cs-orange sm:text-sm sm:leading-6"
            />
          </div>
        </div>
      {:else}
        <div>
          <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          <div class="mt-3 text-center sm:mt-5">
            <h3 class="text-base font-semibold leading-6 text-gray-900" id="modal-title">{$t('common.password_reseted')}</h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500">{$t('common.password_reset_instructions')}</p>
            </div>
          </div>
        </div>
        <div class="mt-5 sm:mt-6">
          <button
            on:click={() => (isRecoverPasswordForm = false)}
            class="flex items-center w-full justify-center rounded-md bg-cs-orange px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-cs-light-orange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cs-orange"
          >
            {$t('common.get_back')}</button
          >
        </div>
      {/if}

      {#if !isRecoverPasswordForm}
        <div>
          <label for="password" class="block text-sm font-medium leading-6 text-gray-900">{$t('common.password')}</label>
          <div class="mt-2">
            <input
              id="password"
              name="password"
              type="password"
              autocomplete="current-password"
              required
              class="block w-full outline-cs-orange rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-cs-orange sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              class="h-4 w-4 rounded border-gray-300 focus:ring-cs-orange outline-cs-orange accent-cs-orange"
            />
            <label for="remember-me" class="ml-3 block text-sm leading-6 text-gray-900">{$t('common.remember_me')}</label>
          </div>

          <div class="text-sm leading-6">
            <button
              type="button"
              on:click={() => {
                isRecoverPasswordForm = true
                errorMessage = undefined
              }}
              class="outline-cs-orange font-semibold text-cs-orange hover:text-orange-500 hover:underline"
              >{$t('common.recover_password')}</button
            >
          </div>
        </div>
      {/if}

      {#if !recoverPasswordSuccess || !isRecoverPasswordForm}
        <div>
          <button
            type="submit"
            formaction={isRecoverPasswordForm ? '?/recoverPassword' : '?/login'}
            class="flex items-center w-full justify-center rounded-md bg-cs-orange px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-cs-light-orange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cs-orange"
          >
            {!isRecoverPasswordForm ? $t('common.login') : $t('common.recover_password')}</button
          >
        </div>
      {/if}
    </form>

    {#if errorMessage}
      <div class="flex justify-center items-center gap-1 my-2">
        <XCircle class="text-red-600" />
        <span class="text-center text-xs font-semibold text-red-600">{errorMessage}</span>
      </div>
    {/if}

    <p class="mt-6 text-center text-sm text-gray-500">
      {$t('common.signup_call')}
      <a href="/register" class="outline-cs-orange font-semibold leading-6 text-cs-orange hover:text-orange-500 hover:underline"
        >{$t('common.signup_now')}</a
      >
    </p>
  </div>
</div>

<style>
  input {
    padding: 6px 12px;
  }
</style>
